import { getAuthHeaders } from './authActions'

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001'

const parseResponse = async (res) => {
	const text = await res.text()
	if (!text) return null
	try {
		return JSON.parse(text)
	} catch {
		return text
	}
}

const fetchWithAuth = async (endpoint, options = {}) => {
	const { method = 'GET', headers = {}, ...rest } = options

	const res = await fetch(`${BASE_URL}${endpoint}`, {
		method,
		headers: {
			...getAuthHeaders(),
			...headers,
		},
		...rest,
	})

	const payload = await parseResponse(res)
	if (!res.ok) {
		const message =
			typeof payload === 'string'
				? payload
				: payload?.message || 'Request failed'
		const error = new Error(message)
		if (payload && typeof payload === 'object') {
			error.payload = payload
			error.summary = payload.summary
			error.details = payload.summary?.details
		}
		throw error
	}

	return payload
}

// GET all orgs
export const getOrgs = () => fetchWithAuth('/api/control/orgs')

// CREATE org
export const createOrg = ({ name, slug }) =>
	fetchWithAuth('/api/control/orgs/create', {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify({ name, slug }),
	})

// UPDATE org
export const updateOrgDetails = async (orgSlug, data) => {
	return fetchWithAuth(`/api/${orgSlug}/org/details`, {
		method: 'PATCH',
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify(data),
	})
}

// --------------------
// Billing control APIs
// --------------------

export const getBillingPlans = async () => {
	const res = await fetchWithAuth('/api/control/billing/plans')
	return res?.plans || []
}

export const assignOrgPlan = async (orgId, planId) => {
	return fetchWithAuth(`/api/control/billing/orgs/${orgId}/assign-plan`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify({ planId }),
	})
}

export const getOrgBillingSettings = async (orgId) => {
	return fetchWithAuth(`/api/control/billing/orgs/${orgId}/billing-settings`)
}

export const updateOrgBillingOverrides = async (orgId, data) => {
	return fetchWithAuth(`/api/control/billing/orgs/${orgId}/billing-overrides`, {
		method: 'PATCH',
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify(data),
	})
}

export const getOrgEffectiveBillingConfig = async (orgId) => {
	const res = await fetchWithAuth(`/api/control/billing/orgs/${orgId}/effective-config`)
	return res?.effectiveConfig
}

export const getOrgUsagePreview = async (orgId, year, month) => {
	const params = new URLSearchParams({
		year: String(year),
		month: String(month),
	})
	const res = await fetchWithAuth(
		`/api/control/billing/orgs/${orgId}/usage-preview?${params.toString()}`,
	)
	return res?.usagePreview
}

export const getOrgInvoicePreview = async (orgId, year, month) => {
	const params = new URLSearchParams({
		year: String(year),
		month: String(month),
	})
	const res = await fetchWithAuth(
		`/api/control/billing/orgs/${orgId}/invoice-preview?${params.toString()}`,
	)
	return res?.invoicePreview
}

export const getOrgInvoiceSnapshots = async (orgId, { limit = 36 } = {}) => {
	const params = new URLSearchParams({
		limit: String(limit),
	})
	const res = await fetchWithAuth(
		`/api/control/billing/orgs/${orgId}/invoice-snapshots?${params.toString()}`,
	)
	return res?.invoices || []
}

export const runOrgMonthEndBilling = async (orgId, { year, month }) => {
	return fetchWithAuth(`/api/control/billing/orgs/${orgId}/run-month-end-billing`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify({ year, month }),
	})
}

export const reconcileOrgStripeBilling = async (orgId) => {
	return fetchWithAuth(`/api/control/billing/orgs/${orgId}/stripe/reconcile`, {
		method: 'POST',
	})
}

export const getOrgPlanLimits = async (orgId) => {
	return fetchWithAuth(`/api/control/billing/orgs/${orgId}/plan-limits`)
}

// --------------------
// Commerce control APIs
// --------------------

export const getOrgCommerceControlSettings = async (orgId) => {
	return fetchWithAuth(`/api/control/commerce/orgs/${orgId}/settings`)
}

export const updateOrgCommercePlatformFees = async (orgId, payload = {}) => {
	return fetchWithAuth(`/api/control/commerce/orgs/${orgId}/platform-fees`, {
		method: 'PATCH',
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify(payload || {}),
	})
}
